import React, { useState } from 'react';
import { withStyles } from '@material-ui/core/styles';
import { useMutation, useQuery } from 'react-query';

import MuiTypography from '@material-ui/core/Typography';
import MuiPaper from '@material-ui/core/Paper';
import MuiFormControlLabel from '@material-ui/core/FormControlLabel';

import MuiGrid from '@material-ui/core/Grid';
import MuiButton from '@material-ui/core/Button';
import MuiToll from '@material-ui/icons/Toll';
import MuiSwitch from '@material-ui/core/Switch';
import MuiCircularProgress from '@material-ui/core/CircularProgress';

import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import LuxonUtils from '@date-io/luxon';

import ViewWrapper from 'views/ViewWrapper';
import TableTrackRevenues from './track-revenues-table';

import { isISRC } from 'utils/helpers';

import * as API from 'api';
import ButtonWithConfirmation from 'components/ButtonWithConfirmation';

const createRowData = row => {
  return {
    id: row.id,
    title: row.artist_title,
    isrc: row.isrc,
    upc: row.upc,
    asset_release_id: row.asset_release_id,
    asset_track_id: row.asset_track_id,
    type: row.type,
    download_us_ca: row.download_us_ca ? 'Yes' : 'No',
    gross_revenue_usd: row.gross_revenue_usd,
    revenue_sortable: parseFloat(row.gross_revenue_usd),
    consumption_count: row.consumption_count,
    royalties_created: row.royalties_created ? 'Yes' : 'No',
    error: row.royalty_creation_error,
  };
};

const styles = theme => ({
  vCenter: {
    display: 'flex',
    alignItems: 'center',
  },
  paper: {
    overflow: 'hidden',
    padding: theme.spacing(2),
  },
  title: {
    paddingBottom: theme.spacing(2),
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    paddingBottom: theme.spacing(2),
  },
  picker: {
    width: 125,
    marginRight: theme.spacing(1),
  },
  actionButton: {
    marginRight: theme.spacing(1),
  },
  toggleLabel: {
    marginRight: 0,
  },
  startLabel: {
    marginRight: theme.spacing(1),
  },
  endLabel: {
    marginLeft: theme.spacing(0.5),
  },
});

const TrackRevenues = ({ ...props }) => {
  const TrackRevenues = API.TrackRevenues();

  const { classes } = props;

  const [showCreated, setShowCreated] = useState(false);
  const [royalityCreationError, setRoyalityCreationError] = useState('');

  const [params, setParams] = useState({
    page: 0,
    rowsPerPage: 250,
    error: 'All',
    isrc: null,
  });

  const {
    isFetching: loadingTrackRevenues,
    data: trackRevenueRows,
    refetch,
  } = useQuery(
    ['TrackRevenues', params?.page, params?.rowsPerPage, params?.error, params?.isrc, showCreated],
    () => {
      return TrackRevenues.getAll({ ...params, royalties_created: showCreated });
    },
    {
      select: data => data.results.map(row => createRowData(row)),
    }
  );

  const { refetch: exportTrackRevenuesData, isFetching: exportLoading } = useQuery(
    ['downloadTrackRevenues'],
    () => TrackRevenues.exportQuery({ ...params, royalties_created: showCreated }),
    {
      staleTime: 0,
      //we want this query to be exclusively manual.
      enabled: false,
      onSuccess: () => {
        alert('Please wait for an email with the export data.');
      },
    }
  );

  const { isMutating: creatingRoyalties, mutate: attemptCreateRoyalties } = useMutation(
    () => TrackRevenues.attemptCreateRoyalties(),

    {
      onSuccess: () => {
        alert('Royalties creation job has been queued.');
      },

      onError: err => {
        setRoyalityCreationError(err.data.detail);
      },
    }
  );

  function handleChangePage(_event, newPage) {
    setParams({ ...params, page: newPage });
  }

  function handleChangeRowsPerPage({ target: { value: newRowsPerPage } }) {
    setParams({ ...params, page: 0, rowsPerPage: newRowsPerPage });
  }

  function handleChangeError(error) {
    setParams({ ...params, page: 0, error });
  }

  function handleISRCQuery(isrc) {
    //If the values is not an ISRC it doesn't attempt to query.
    //also because of how react-query works it normally has the original state cached
    setParams({ ...params, isrc: isISRC(isrc) ? isrc : null });
  }

  const handleCreateRoyaltiesClick = async () => {
    attemptCreateRoyalties();
  };

  const handleShowCreatedChanged = e => {
    setShowCreated(!e.target.checked);
  };

  return (
    <ViewWrapper>
      <MuiGrid item xs={12}>
        <MuiPaper className={classes.paper}>
          <MuiTypography className={classes.title} variant="h5">
            Track Revenues
          </MuiTypography>
          <div className={classes.actionRow}>
            <MuiPickersUtilsProvider utils={LuxonUtils}>
              <div className={classes.vCenter}>
                <ButtonWithConfirmation
                  size="small"
                  variant="contained"
                  className={classes.actionButton}
                  startIcon={creatingRoyalties ? <MuiCircularProgress size={'1rem'} /> : <MuiToll />}
                  buttonText="Create Royalties"
                  dialogTitle="Create Royalties"
                  dialogContent="Are you sure you want to create royalties?"
                  onSubmit={handleCreateRoyaltiesClick}
                  disabled={showCreated || creatingRoyalties}
                />
                <span>{royalityCreationError}</span>
              </div>
              <div className={classes.vCenter}>
                <MuiButton
                  size="small"
                  variant="contained"
                  startIcon={exportLoading ? <MuiCircularProgress size={'1rem'} /> : null}
                  onClick={exportTrackRevenuesData}
                  disabled={exportLoading}>
                  CSV Export
                </MuiButton>
              </div>

              <div>
                <span className={classes.startLabel}>Created</span>
                <MuiFormControlLabel
                  className={classes.toggleLabel}
                  control={
                    <MuiSwitch
                      checked={!showCreated}
                      onChange={handleShowCreatedChanged}
                      name="showCreated"
                      inputProps={{ 'aria-label': 'secondary checkbox' }}
                    />
                  }
                />
                <span className={classes.endLabel}>Not created</span>
              </div>
            </MuiPickersUtilsProvider>
          </div>
          <TableTrackRevenues
            handleISRCQuery={handleISRCQuery}
            handleChangePage={handleChangePage}
            handleChangeRowsPerPage={handleChangeRowsPerPage}
            handleChangeError={handleChangeError}
            trackRevenues={trackRevenueRows || []}
            filters={{ showCreated }}
            params={params}
            setParams={setParams}
            total={-1}
            isLoading={loadingTrackRevenues}
          />
        </MuiPaper>
      </MuiGrid>
    </ViewWrapper>
  );
};

export default withStyles(styles)(TrackRevenues);
